import React, { useState } from 'react';
import { LeadSuggestion, leadSuggestionService } from '../services/leadSuggestionService';

interface EmailSuggestionModalProps {
  open: boolean;
  onClose: () => void;
  suggestion: LeadSuggestion | null;
  clientId?: string;
  leadId?: string;
  contextLabel?: string;
}

const emailTypeOptions = [
  { id: 'intro', label: 'Intro Email' },
  { id: 'follow_up', label: 'Follow-Up Email' },
  { id: 're_engagement', label: 'Re-engagement Email' },
  { id: 'estimate_follow_up', label: 'Estimate Follow-Up' },
  { id: 'appointment_request', label: 'Appointment Request' },
];

const EmailSuggestionModal: React.FC<EmailSuggestionModalProps> = ({
  open,
  onClose,
  suggestion,
  clientId,
  leadId,
  contextLabel,
}) => {
  const [emailType, setEmailType] = useState('intro');
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cta, setCta] = useState('');
  const [emailSuggestionId, setEmailSuggestionId] = useState<string | null>(null);

  if (!open || (!suggestion && !clientId && !leadId)) return null;

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await leadSuggestionService.generateEmailSuggestion({
        email_type: emailType,
        lead_suggestion_id: suggestion?.id,
        client_id: clientId,
        lead_id: leadId,
      });
      setSubject(result.subject || '');
      setBody(result.body || '');
      setCta(result.cta_suggestion || '');
      setEmailSuggestionId(result.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate email suggestion');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}${cta ? `\n\nCTA: ${cta}` : ''}`);
  };

  const saveDraft = async () => {
    if (!emailSuggestionId) return;
    setSavingDraft(true);
    setError('');
    try {
      await leadSuggestionService.saveEmailDraft({ email_suggestion_id: emailSuggestionId });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl rounded-xl border border-border bg-app p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-fg">Generate Email Suggestion</h2>
            <p className="text-sm text-fg-muted">{contextLabel || suggestion?.company_name || 'Lead context'}</p>
          </div>
          <button type="button" onClick={onClose} className="text-fg-muted hover:text-fg">
            Close
          </button>
        </div>

        <div className="flex gap-2 items-end">
          <label className="flex-1">
            <span className="block text-sm text-fg-muted mb-1">Email Type</span>
            <select
              value={emailType}
              onChange={(e) => setEmailType(e.target.value)}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-fg"
            >
              {emailTypeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={generate} disabled={loading} className="btn-primary px-4 py-2 text-sm">
            {loading ? 'Generating...' : 'Generate'}
          </button>
        </div>

        {error && <div className="text-sm text-red-400">{error}</div>}

        <label className="block">
          <span className="block text-sm text-fg-muted mb-1">Subject</span>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-fg"
            placeholder="Generated subject line"
          />
        </label>

        <label className="block">
          <span className="block text-sm text-fg-muted mb-1">Body</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-fg"
            placeholder="Generated message body"
          />
        </label>

        <label className="block">
          <span className="block text-sm text-fg-muted mb-1">Optional CTA</span>
          <input
            value={cta}
            onChange={(e) => setCta(e.target.value)}
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-fg"
            placeholder="Call to action"
          />
        </label>

        <div className="flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={copyToClipboard} className="px-3 py-2 text-sm rounded border border-border text-fg">
            Copy Email
          </button>
          <button type="button" onClick={saveDraft} disabled={!emailSuggestionId || savingDraft} className="btn-primary px-3 py-2 text-sm">
            {savingDraft ? 'Saving Draft...' : 'Save Draft'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailSuggestionModal;
